import { Address } from "@dcspark/cardano-multiplatform-lib-nodejs";
import { bytesToHex } from "@sundae/cardano-utils";
import { addressToBech32, addressToStakeCredentials, } from "@sundae/serialization";
import { useMutation, useQuery, } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { DEFAULT_REFRESH_RATE } from "../constants/fallbacks";
import { Actions, useWalletContext } from "../store";
import { displayError } from "../utils/displayError";
import { useHeartbeatLogger } from "./useHeartbeatLogger";
import { useWalletApi } from "./useWalletApi";
import { useWalletState } from "./useWalletState";
const HOOK_CONTEXT = "useWalletDetails";
export const DETAILS_MUTATION_KEY = "cardano.details";
export const useWalletDetailsMutation = () => {
    const { state, dispatch } = useWalletContext();
    const hb = useHeartbeatLogger();
    const query = useMutation([DETAILS_MUTATION_KEY, state.activeWallet], async ({ api }) => {
        hb.logCheckingWallet(HOOK_CONTEXT);
        if (!api) {
            hb.logNoApi(HOOK_CONTEXT);
            // If we already have existing data, we don't want to override it.
            return state.details || null;
        }
        const [freshUsedAddresses, freshUnusedAddresses, freshNetwork] = await Promise.all([
            api.getUsedAddresses(),
            api.getUnusedAddresses(),
            api.getNetworkId(),
        ]);
        const { staking, payment } = addressToStakeCredentials(freshUsedAddresses[0] ?? freshUnusedAddresses[0]);
        const stakingAddressBech32 = addressToBech32(`${freshNetwork ? "e1" : "e0"}${staking}`);
        const mainAddressBech32 = freshUsedAddresses[0] ?? freshUnusedAddresses[0];
        const data = {
            network: freshNetwork,
            mainAddress: mainAddressBech32,
            stakingAddress: stakingAddressBech32,
            usedAddresses: freshUsedAddresses,
            unusedAddresses: freshUnusedAddresses,
            credentials: {
                staking,
                payment,
            },
            hex: {
                stakingAddress: bytesToHex(Address.from_bech32(stakingAddressBech32).to_bytes()),
                mainAddress: bytesToHex(Address.from_bech32(mainAddressBech32).to_bytes()),
            },
        };
        return data;
    }, {
        onSuccess: (newWalletData) => {
            if (!newWalletData || isEqual(state.details, newWalletData)) {
                hb.logDoneWithoutChanges(HOOK_CONTEXT);
                return;
            }
            Actions.setWalletDetails(newWalletData, dispatch);
            hb.logDoneWithChanges(HOOK_CONTEXT);
        },
        onSettled() {
            if (!state.initialized.details) {
                Actions.setInitialized({ details: true }, dispatch);
            }
        },
    });
    return query;
};
export const useWalletDetails = (options) => {
    const { loaded } = useWalletState();
    const { state, dispatch } = useWalletContext();
    const { api, query: apiQuery, connectApi } = useWalletApi();
    const { mutateAsync } = useWalletDetailsMutation();
    const query = useQuery([DETAILS_MUTATION_KEY, api], async () => mutateAsync({ api }), {
        refetchOnWindowFocus: "always",
        refetchInterval: state.options?.refreshRates?.details || DEFAULT_REFRESH_RATE,
        refetchOnMount: false,
        initialData: state.details,
        onError: async (e) => {
            console.log(e);
            if (e?.code === -4) {
                apiQuery.reset();
                connectApi({ overrideWallet: false });
            }
            else {
                Actions.setInitialized({ details: false }, dispatch);
                Actions.setError(await displayError(e), dispatch);
            }
        },
        ...options,
    });
    return {
        activeWallet: state.activeWallet,
        dAppConnector: state.dAppConnector,
        details: query.data || {},
        isFetched: query.isSuccess,
        isLoaded: loaded.details,
        query,
        strategy: state.options.strategy,
    };
};
//# sourceMappingURL=useWalletDetails.js.map